// ═══════════════════════════════════════════════════════════════
// CULTIVATION VIEW — Thin tab router
// Path: src/views/CultivationView.jsx
// Tabs: Overview | Pipeline | Actions | Members | Comms
// ═══════════════════════════════════════════════════════════════

import React, { useState, useMemo, useEffect } from "react";
import {
  Sprout,
  Eye,
  TrendingUp,
  Zap,
  Users,
  Send,
  Search,
  X,
  ClipboardList,
} from "lucide-react";
import { useApp } from "../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../constants/appConfig";
import { ViewLayout } from "../components/ui/ViewLayout";
import { HELP } from "../constants/helpContent";
import {
  ZoneOneHeader,
  ZoneThreeFooter,
} from "../components/zones/ZoneHeaderFooter";
import { ZoneTwoToolbar } from "../components/zones/ZoneTwoToolbar";
import {
  enrichMembers,
  segmentMembers,
  buildPipelineBuckets,
  getPipelineAction,
} from "../utils/cultivationUtils";

// Tab components
import { CultivationOverviewTab } from "../components/cultivation/CultivationOverviewTab";
import { CultivationPipelineTab } from "../components/cultivation/CultivationPipelineTab";
import { CultivationActionsTab } from "../components/cultivation/CultivationActionsTab";
import { CultivationMembersTab } from "../components/cultivation/CultivationMembersTab";
import { CultivationCommsTab } from "../components/cultivation/CultivationCommsTab";
import { CultivationQueueDrawer } from "../components/cultivation/CultivationQueueDrawer";

// ── Tab Definitions ───────────────────────────────────────────

const CULTIVATION_TABS = [
  { id: "overview", label: "Overview", icon: Eye },
  { id: "pipeline", label: "Pipeline", icon: TrendingUp },
  { id: "actions", label: "Actions", icon: Zap },
  { id: "members", label: "Donors", icon: Users },
  { id: "comms", label: "Comms", icon: Send },
];

// ═══════════════════════════════════════════════════════════════
// MAIN VIEW
// ═══════════════════════════════════════════════════════════════

export const CultivationView = () => {
  const { palette, members, pathways, personas, goBack, goHome, navigate, cultivationQueue, allGifts, allEventAttendance, allSponsorships } =
    useApp();
  const [showQueue, setShowQueue] = useState(false);

  // ── Tab State (persisted to session) ────────────────────────
  const [activeTab, setActiveTab] = useState(() => {
    try {
      return sessionStorage.getItem("cmm_cultivation_tab") || "overview";
    } catch {
      return "overview";
    }
  });
  useEffect(() => {
    try {
      sessionStorage.setItem("cmm_cultivation_tab", activeTab);
    } catch {}
  }, [activeTab]);

  // ── Search & Filter (shared across tabs) ────────────────────
  const [pipelineSearch, setPipelineSearch] = useState("");
  const [pipelineFilterDirector, setPipelineFilterDirector] = useState("all");

  // ── Enriched Data (computed once, shared across all tabs) ───
  const enriched = useMemo(
    () => enrichMembers(members, pathways, personas, { allGifts, allEventAttendance, allSponsorships }),
    [members, pathways, allGifts, allEventAttendance, allSponsorships]
  );
  const segments = useMemo(() => segmentMembers(enriched), [enriched]);
  const pipelineBuckets = useMemo(
    () => buildPipelineBuckets(enriched, pipelineFilterDirector, pipelineSearch),
    [enriched, pipelineFilterDirector, pipelineSearch]
  );

  const directorList = useMemo(
    () => [
      ...new Set(
        members.map((m) => m.assignedDirector).filter((m) => m?.trim())
      ),
    ],
    [members]
  );

  const pipelineOverdueCount = useMemo(
    () =>
      enriched.filter(
        (m) => getPipelineAction(m, m.engagement).urgency === "overdue"
      ).length,
    [enriched]
  );

  const pipelineTotalShowing = Object.values(pipelineBuckets).reduce(
    (sum, arr) => sum + arr.length,
    0
  );

  const handleMemberClick = (member) => navigate("memberDetail", member);

  // ═══════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════

  return (
    <ViewLayout>
      <ZoneOneHeader
        title="Cultivation"
        subtitle={`Engagement intelligence for ${members.length} donors`}
        icon={Sprout}
        onBack={goBack}
        onHome={goHome}
        helpContent={HELP.cultivation}
      />
      <ZoneTwoToolbar />

      {/* ── Zone 2: Search + Tabs + Filter ───────────────────── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px 24px",
          gap: 12,
          flexWrap: "wrap",
          background: palette.toolbarBg,
          borderBottom: `1px solid ${palette.cardBorder}`,
          fontFamily: FONT_FAMILY,
        }}
      >
        {/* Search */}
        <div style={{ position: "relative", flex: "1 1 220px", maxWidth: 320 }}>
          <Search
            size={ICON_SIZES.sm}
            style={{
              position: "absolute",
              left: 10,
              top: "50%",
              transform: "translateY(-50%)",
              color: palette.text3,
              pointerEvents: "none",
            }}
          />
          <input
            value={pipelineSearch}
            onChange={(e) => setPipelineSearch(e.target.value)}
            placeholder="Search by name, org, director..."
            style={{
              width: "100%",
              padding: "8px 34px 8px 34px",
              borderRadius: 8,
              fontSize: FONT_SIZES.sm,
              fontFamily: FONT_FAMILY,
              outline: "none",
              background: palette.inputBg,
              color: palette.inputColor,
              border: `1px solid ${palette.inputBorder}`,
              transition: "all 0.2s",
            }}
            onFocus={(e) => {
              e.target.style.borderColor =
                palette.focusBorder || palette.accent1;
              e.target.style.boxShadow =
                palette.focusShadow || `0 0 0 3px ${palette.accent1}20`;
            }}
            onBlur={(e) => {
              e.target.style.borderColor = palette.inputBorder;
              e.target.style.boxShadow = "none";
            }}
          />
          {pipelineSearch && (
            <button
              onClick={() => setPipelineSearch("")}
              title="Clear search"
              style={{
                position: "absolute",
                right: 8,
                top: "50%",
                transform: "translateY(-50%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 20,
                height: 20,
                borderRadius: "50%",
                background: "#6b728020",
                border: "none",
                cursor: "pointer",
                padding: 0,
                transition: "background 0.15s",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = "#6b728040")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = "#6b728020")
              }
            >
              <X size={ICON_SIZES.xs} style={{ color: "#6b7280" }} />
            </button>
          )}
        </div>

        {/* Tab Nav */}
        <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
          {CULTIVATION_TABS.map((tab) => {
            const isActive = activeTab === tab.id;
            const TabIcon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                  padding: "6px 14px",
                  borderRadius: 8,
                  border: "none",
                  cursor: "pointer",
                  fontFamily: FONT_FAMILY,
                  background: isActive ? `${palette.accent1}15` : "transparent",
                  transition: "all 0.15s",
                }}
                onMouseEnter={(e) => {
                  if (!isActive)
                    e.currentTarget.style.background = `${palette.accent1}08`;
                }}
                onMouseLeave={(e) => {
                  if (!isActive)
                    e.currentTarget.style.background = "transparent";
                }}
              >
                <TabIcon
                  size={ICON_SIZES.md}
                  style={{
                    color: isActive ? palette.accent1 : palette.text3,
                    transition: "color 0.15s",
                  }}
                />
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: isActive ? 700 : 500,
                    color: isActive ? palette.accent1 : palette.text3,
                    letterSpacing: 0.2,
                  }}
                >
                  {tab.label}
                </span>
              </button>
            );
          })}
        </div>

        {/* Director Filter + Stats */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button
            onClick={() => setShowQueue(true)}
            title="Cultivation Queue"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              padding: "5px 10px",
              borderRadius: 8,
              border: `1px solid ${palette.inputBorder}`,
              background: cultivationQueue.length > 0 ? `#10b98110` : palette.inputBg,
              cursor: "pointer",
              fontFamily: FONT_FAMILY,
              fontSize: FONT_SIZES.xs,
              fontWeight: 600,
              color: palette.text1,
            }}
          >
            <ClipboardList size={ICON_SIZES.sm} />
            Queue
            {cultivationQueue.length > 0 && (
              <span
                style={{
                  background: "#10b981",
                  color: "#fff",
                  fontSize: 10,
                  fontWeight: 700,
                  padding: "1px 6px",
                  borderRadius: 8,
                  marginLeft: 2,
                }}
              >
                {cultivationQueue.length}
              </span>
            )}
          </button>
          {pipelineOverdueCount > 0 && (
            <span
              style={{
                background: "#dc2626",
                color: "#fff",
                fontSize: 11,
                fontWeight: 700,
                padding: "3px 10px",
                borderRadius: 12,
              }}
            >
              {pipelineOverdueCount} overdue
            </span>
          )}
          <span
            style={{
              fontSize: FONT_SIZES.sm,
              color: palette.text2,
              fontWeight: 500,
            }}
          >
            {pipelineTotalShowing} of {members.length}
          </span>
          <select
            value={pipelineFilterDirector}
            onChange={(e) => setPipelineFilterDirector(e.target.value)}
            style={{
              background: palette.inputBg,
              color: palette.inputColor,
              border: `1px solid ${palette.inputBorder}`,
              borderRadius: 8,
              padding: "7px 12px",
              fontSize: FONT_SIZES.sm,
              fontFamily: FONT_FAMILY,
              cursor: "pointer",
              outline: "none",
            }}
          >
            <option value="all">All Directors</option>
            {directorList.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* ── Content Area ─────────────────────────────────────── */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            flex: 1,
            padding: activeTab === "pipeline" ? 0 : 24,
            overflowY: activeTab === "pipeline" ? "hidden" : "auto",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {activeTab === "overview" && (
            <CultivationOverviewTab
              enriched={enriched}
              segments={segments}
              onMemberClick={handleMemberClick}
            />
          )}

          {activeTab === "pipeline" && (
            <CultivationPipelineTab
              pipelineBuckets={pipelineBuckets}
              enriched={enriched}
            />
          )}

          {activeTab === "actions" && (
            <CultivationActionsTab
              enriched={enriched}
              onMemberClick={handleMemberClick}
            />
          )}

          {activeTab === "members" && (
            <CultivationMembersTab
              enriched={enriched}
              onMemberClick={handleMemberClick}
            />
          )}

          {activeTab === "comms" && <CultivationCommsTab enriched={enriched} />}
        </div>
      </div>

      <ZoneThreeFooter />
      <CultivationQueueDrawer isOpen={showQueue} onClose={() => setShowQueue(false)} />
    </ViewLayout>
  );
};
